import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CssBaseline } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../data/theme";

const STORAGE_KEY = "SystemTheme";

const normalizeMode = (value) => {
  if (typeof value !== "string") return null;
  const normalized = value.toLowerCase();
  if (normalized === "light" || normalized === "dark") return normalized;
  return null;
};

const ThemeContext = createContext({
  mode: "dark",
  setMode: () => {},
  toggleMode: () => {},
});

export const ThemeContextProvider = ({ children, defaultMode = "dark" }) => {
  const [mode, setModeState] = useState(defaultMode);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const stored = window.localStorage?.getItem(STORAGE_KEY);
    const normalized = normalizeMode(stored);
    if (normalized) setModeState(normalized);

    const onStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      const next = normalizeMode(event.newValue);
      if (next) setModeState(next);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((nextMode) => {
    const normalized = normalizeMode(nextMode);
    if (!normalized) return;

    setModeState(normalized);

    if (typeof window === "undefined") return;
    window.localStorage?.setItem(STORAGE_KEY, normalized);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeContextProvider.propTypes = {
  children: PropTypes.any,
  defaultMode: PropTypes.oneOf(["light", "dark"]),
};

export const useThemeMode = () => useContext(ThemeContext);

export default ThemeContext;
